
import { Strategy } from "@/types/strategy-types";
import { toast } from "@/components/ui/use-toast";
import { NavigateFunction } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useStripeSubscriptionCreation } from "./stripe/useStripeSubscriptionCreation";
import { useStrategyFollowHandler } from "./strategy/useStrategyFollowHandler";
import { processReferral } from "@/utils/paymentReferralUtils";
import { useClientLocation } from "@/hooks/use-client-location";

interface UseStripePaymentProps {
  strategy?: Strategy;
  isYearly?: boolean;
  transformStrategy: (strategy?: Strategy) => any;
  setIsProcessing: (value: boolean) => void;
  setPaymentError: (error: string | null) => void;
  navigate: NavigateFunction;
}

export const useStripePayment = ({
  strategy,
  isYearly,
  transformStrategy,
  setIsProcessing,
  setPaymentError,
  navigate
}: UseStripePaymentProps) => {
  const { createStripeSubscription } = useStripeSubscriptionCreation();
  const { followStrategy } = useStrategyFollowHandler();
  const clientLocation = useClientLocation();

  const handleStripePayment = async (paymentMethod: any, promotionCode?: string) => {
    setIsProcessing(true);
    setPaymentError(null);
    
    try {
      console.log("----------- PAYMENT ATTEMPT START -----------");
      console.log("Payment method:", paymentMethod.id);
      if (promotionCode) {
        console.log("Promotion code applied:", promotionCode);
      }
      
      // Log client location data for debugging
      console.log("Client location data:", {
        country_code: clientLocation.country_code,
        country: clientLocation.country,
        loading: clientLocation.loading
      });
      
      // Create subscription with Stripe and get subscription data
      const subscriptionData = await createStripeSubscription(
        paymentMethod, 
        isYearly, 
        promotionCode,
        clientLocation.country_code // Pass country code for tax rate
      );
      
      console.log("Subscription created successfully:", subscriptionData.id);
      
      // Process referral first, independent of strategy
      const currentUser = await supabase.auth.getUser();
      if (currentUser.data.user) {
        console.log("Processing referral for user:", currentUser.data.user.id);
        await processReferral(currentUser.data.user.id, subscriptionData.id);
      }

      // Follow strategy if one was selected
      if (strategy) {
        await followStrategy(strategy, transformStrategy);
      }

      // Show success message with discount information if applicable
      const successMessage = subscriptionData.discount 
        ? `Payment successful! ${subscriptionData.discount.percent_off 
            ? `${subscriptionData.discount.percent_off}% discount` 
            : `$${(subscriptionData.discount.amount_off/100).toFixed(2)} discount`} applied.`
        : "Payment successful! 🎉";
        
      toast({
        title: "Payment successful! 🎉",
        description: successMessage + " Welcome to Metrade Premium! You can now follow premium strategies.",
        duration: 5000
      });

      console.log("Payment successful, navigating to thank-you page");
      setTimeout(() => {
        navigate('/thank-you', {
          state: {
            strategy,
            isYearly
          }
        });
      }, 500);
    } catch (error: any) {
      console.error("Subscription error:", error);
      const errorMessage = error.message || "Please try again or contact support.";
      setPaymentError(errorMessage);
      toast({
        title: "Payment failed",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      console.log("----------- PAYMENT ATTEMPT END -----------");
      setIsProcessing(false);
    }
  };

  return { handleStripePayment };
};
